import { isbot } from 'isbot';
import type { ExperimentApi, ExperimentStatePort, ExperimentTelemetry } from './ports.js';
import type { ExperimentEntry, ExperimentRequestContext, GetExperimentsPayload } from './types.js';

/** UA suffix used by classic/Playwright/WDIO to mark automated test sessions. */
const AUTOMATED_TESTING_UA_MARKER = 'isAutomatedTesting:true';

/**
 * Construction dependencies for {@link ExperimentClient}.
 * `TEventMetadata` is the client-defined `event_metadata` shape.
 */
export interface ExperimentClientConfig<TEventMetadata = unknown> {
	api: ExperimentApi<TEventMetadata>;
	state: ExperimentStatePort;
	telemetry?: ExperimentTelemetry;
}

export interface GetAssignmentParams<TEventMetadata = unknown> {
	autoMarkSeen: boolean;
	context: ExperimentRequestContext<TEventMetadata>;
	experimentKey: string;
	fallbackVariant: string;
}

/** One requested key in a bulk Get, with the variant to retain when it is unavailable. */
export interface AssignmentRequest {
	experimentKey: string;
	fallbackVariant: string;
}

export interface GetAssignmentsParams<TEventMetadata = unknown> {
	autoMarkSeen: boolean;
	context: ExperimentRequestContext<TEventMetadata>;
	experiments: AssignmentRequest[];
}

/**
 * Framework-agnostic experiment client: Get one assignment at a divergence point
 * or several at once, with auto_mark_seen, bot handling, and synchronous variant
 * lookups against injected state. The host supplies the per-call request context;
 * this client only builds the payload envelope and merges results back into state.
 */
export class ExperimentClient<TEventMetadata = unknown> {
	readonly #api: ExperimentApi<TEventMetadata>;
	readonly #state: ExperimentStatePort;
	readonly #telemetry?: ExperimentTelemetry;
	#writeQueue: Promise<unknown> = Promise.resolve();

	constructor({ api, state, telemetry }: ExperimentClientConfig<TEventMetadata>) {
		this.#api = api;
		this.#state = state;
		this.#telemetry = telemetry;
	}

	/**
	 * Returns a previously loaded assignment from memory, or gets one from the
	 * experiment API. Any missing or failed assignment resolves to the explicit
	 * fallback and is retained in memory for the current client session.
	 */
	async getAssignment({
		autoMarkSeen,
		context,
		experimentKey,
		fallbackVariant,
	}: GetAssignmentParams<TEventMetadata>): Promise<string> {
		if (!experimentKey) {
			this.#captureFallback('ExperimentClient: experiment key missing; using fallback', {
				fallback_variant: fallbackVariant,
			});
			return fallbackVariant;
		}

		const storedExperiment = this.#findExperiment(experimentKey);
		if (storedExperiment?.variant && (!autoMarkSeen || storedExperiment.seen)) {
			return storedExperiment.variant;
		}

		if (this.#isBotSession()) {
			await this.#storeFallback(experimentKey, fallbackVariant);
			return fallbackVariant;
		}

		const expFallbackMap = new Map([[experimentKey, fallbackVariant]]);
		const payload: GetExperimentsPayload<TEventMetadata> = {
			auto_mark_seen: autoMarkSeen,
			data: context.data,
			event_metadata: context.eventMetadata,
			experiment_keys: [experimentKey],
			profile: context.profile,
			user: context.user,
		};
		const { experiments, returnedExperimentKeys: returnedKeys } = await this.#fetchAndMerge(
			context.host,
			payload,
			expFallbackMap
		);
		const experiment = experiments.find((entry) => entry.key === experimentKey);
		if (returnedKeys.has(experimentKey) && experiment?.variant) return experiment.variant;

		this.#captureFallback('ExperimentClient: assignment missing; using fallback', {
			experiment_key: experimentKey,
			fallback_variant: fallbackVariant,
		});
		return fallbackVariant;
	}

	/**
	 * Gets several assignments in one Get. Memory only skips the Get when every
	 * requested key has already been seen; otherwise the whole batch is refreshed.
	 * Bot sessions, unavailable keys, and API failures retain each key's fallback.
	 */
	async getAssignments({
		autoMarkSeen,
		context,
		experiments,
	}: GetAssignmentsParams<TEventMetadata>): Promise<ExperimentEntry[]> {
		const expFallbackMap = new Map<string, string>();
		for (const { experimentKey, fallbackVariant } of experiments) {
			if (experimentKey && !expFallbackMap.has(experimentKey)) {
				expFallbackMap.set(experimentKey, fallbackVariant);
			}
		}
		const requestedExps = [...expFallbackMap.keys()];
		if (requestedExps.length === 0) return this.#state.getExperiments() ?? [];

		if (requestedExps.every((key) => this.#findExperiment(key)?.seen === true)) {
			return this.#state.getExperiments() ?? [];
		}

		if (this.#isBotSession()) return this.#storeFallbacks(expFallbackMap);

		const payload: GetExperimentsPayload<TEventMetadata> = {
			auto_mark_seen: autoMarkSeen,
			data: context.data,
			event_metadata: context.eventMetadata,
			experiment_keys: requestedExps,
			profile: context.profile,
			user: context.user,
		};
		const { experiments: merged } = await this.#fetchAndMerge(
			context.host,
			payload,
			expFallbackMap
		);
		return merged;
	}

	isVariantActive(experimentKey: string, variant: string): boolean {
		const experiments = this.#state.getExperiments();
		if (!experiments?.length || !experimentKey) return false;
		const experiment = experiments.find((exp) => exp.key === experimentKey);
		return experiment?.variant === variant;
	}

	isAnyVariantActive(experimentKey: string, variants: string[]): boolean {
		const experiments = this.#state.getExperiments();
		if (!experiments?.length || !experimentKey || !Array.isArray(variants)) return false;
		const experiment = experiments.find((exp) => exp.key === experimentKey);
		return experiment?.variant != null && variants.includes(experiment.variant);
	}

	isAnyAssigned(experimentKeys: string[]): boolean {
		const experiments = this.#state.getExperiments();
		if (!Array.isArray(experimentKeys) || !experimentKeys.length || !experiments?.length) {
			return false;
		}
		for (const key of experimentKeys) {
			const experiment = experiments.find((exp) => exp.key === key);
			if (experiment?.assigned || typeof experiment?.experiment_id === 'string') return true;
		}
		return false;
	}

	#findExperiment(experimentKey: string): ExperimentEntry | undefined {
		if (!experimentKey) return undefined;
		return this.#state.getExperiments()?.find((experiment) => experiment.key === experimentKey);
	}

	#isBotSession(): boolean {
		const userAgent = typeof navigator !== 'undefined' ? (navigator?.userAgent ?? '') : '';
		return !userAgent.includes(AUTOMATED_TESTING_UA_MARKER) && isbot(userAgent);
	}

	/**
	 * Serializes `write` so the next merge reads after `setExperiments` settles.
	 * A rejected write still releases the queue.
	 */
	#enqueueWrite<T>(write: () => Promise<T>): Promise<T> {
		const next = this.#writeQueue.then(write);
		this.#writeQueue = next.catch(() => undefined);
		return next;
	}

	async #storeFallback(experimentKey: string, fallbackVariant: string): Promise<void> {
		await this.#storeFallbacks(new Map([[experimentKey, fallbackVariant]]));
	}

	async #storeFallbacks(expFallbackMap: Map<string, string>): Promise<ExperimentEntry[]> {
		return this.#enqueueWrite(async () => {
			const experiments = new Map(
				(this.#state.getExperiments() ?? []).map((experiment) => [experiment.key, experiment])
			);
			for (const [experimentKey, fallbackVariant] of expFallbackMap) {
				experiments.set(experimentKey, this.#fallbackEntry(experimentKey, fallbackVariant));
			}
			const merged = [...experiments.values()];
			await this.#state.setExperiments(merged);
			return merged;
		});
	}

	#fallbackEntry(experimentKey: string, fallbackVariant: string): ExperimentEntry {
		return {
			assigned: false,
			experiment_id: null,
			key: experimentKey,
			seen: false,
			variant: fallbackVariant,
		};
	}

	#captureFallback(message: string, extra: Record<string, unknown>): void {
		this.#telemetry?.captureMessage(message, { level: 'error', extra });
	}

	/**
	 * Requested keys the API omits are stored as that key's fallback.
	 * A transport failure stores every fallback.
	 */
	async #fetchAndMerge(
		host: string,
		payload: GetExperimentsPayload<TEventMetadata>,
		expFallbackMap: Map<string, string>
	): Promise<{ experiments: ExperimentEntry[]; returnedExperimentKeys: Set<string> }> {
		try {
			const response = await this.#api.getExperiments(host, payload);
			const newExperiments = response?.results ?? [];

			const autoMarkSeen = payload.auto_mark_seen === true;
			const experimentsForStore: ExperimentEntry[] = newExperiments.map((exp) => ({
				assigned: exp.assigned ?? false,
				experiment_id: exp.experiment_id ?? null,
				key: exp.key,
				seen: autoMarkSeen,
				variant: exp.variant,
			}));

			const returnedExperimentKeys = new Set(newExperiments.map((exp) => exp.key));
			const experiments = await this.#enqueueWrite(async () => {
				const currentExperiments = this.#state.getExperiments() ?? [];
				const experimentsMap = new Map<string, ExperimentEntry>();
				for (const exp of currentExperiments) experimentsMap.set(exp.key, { ...exp });
				for (const exp of experimentsForStore) experimentsMap.set(exp.key, exp);
				for (const [key, fallbackVariant] of expFallbackMap) {
					if (returnedExperimentKeys.has(key)) continue;
					experimentsMap.set(key, this.#fallbackEntry(key, fallbackVariant));
				}
				const merged = [...experimentsMap.values()];
				await this.#state.setExperiments(merged);
				return merged;
			});
			return { experiments, returnedExperimentKeys };
		} catch (error) {
			this.#telemetry?.captureException(error, {
				extra: { action: 'Error: Failed To Get Experiments' },
			});
			return {
				experiments: await this.#storeFallbacks(expFallbackMap),
				returnedExperimentKeys: new Set(),
			};
		}
	}
}
